import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { downloadHtmlDocument, openHtmlDocumentForPrint } from '../../shared/print-html';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse, CatalogWarehouseBin } from '../catalog/catalog.service';
import { AccountingCustomerRecord, FinanceApiService } from '../finance/finance-api.service';
import {
  CreateSalesOrderPayload,
  PolicyEvaluationResult,
  PriceListListItem,
  PricingHintRecord,
  PricingOverrideRecord,
  PricingQuoteResult,
  SalesApiService,
  SalesChannelRecord,
  SalesDocumentProfileRecord,
  SalesOrderInventoryStatus,
  SalesOrderLinePayload,
  SalesOrderRecord,
  SalesOrderStateHistoryRecord,
  UpdateSalesOrderPayload
} from './sales-api.service';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';
import { bindSalesOrdersLiveUpdates } from './sales-live-updates';

type SelectOption = { label: string; value: string };

@Component({
  standalone: true,
  selector: 'ain-sales-order-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TableModule,
    DialogModule,
    TextareaModule,
    CurrencySelectorComponent,
    CustomerSelectorComponent,
    AinDatePipe,
    SalesAiCopilotPanelComponent,
    AinCanAccessDirective,
    InfoHintComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="pageDescription()">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <header class="order-header card">
          <div class="toolbar">
            <a class="back-link" [routerLink]="backRouterLink()" [queryParams]="backQueryParams()">{{ backLinkLabel() }}</a>
            <div class="actions">
              @if (!isCreateMode()) {
                <button pButton type="button" severity="secondary" [label]="t('sales.orders.action.printDocument')" (click)="printDocument()" [disabled]="saving()"></button>
                <button pButton type="button" severity="secondary" [label]="t('sales.orders.action.downloadDocument')" (click)="downloadDocument()" [disabled]="saving()"></button>
              }
              @if (!isCreateMode() && status() === 'Draft') {
                <button pButton type="button" [label]="t('sales.orders.action.submit')" (click)="submit()" [disabled]="saving()"></button>
              }
              @if (!isCreateMode() && status() !== 'Cancelled') {
                <button pButton type="button" severity="danger" [label]="t('sales.orders.action.cancel')" (click)="cancel()" [disabled]="saving()"></button>
              }
              @if (canConvertProforma()) {
                <button pButton type="button" severity="secondary" [label]="t('sales.quotes.action.convertToInvoice')" (click)="convertToInvoice()" [disabled]="saving()"></button>
                <button pButton type="button" severity="secondary" [label]="t('sales.quotes.action.convertToContract')" (click)="convertToContract()" [disabled]="saving()"></button>
              }
              @if (canFulfill()) {
                <button pButton type="button" severity="help" ainCanAccess [ainCanAccessPermission]="'sales.orders.fulfill'" [label]="fulfillActionLabel()" (click)="fulfill()" [disabled]="saving() || inventoryBusy()"></button>
              }
              @if (canEdit()) {
                <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="saving()"></button>
              }
            </div>
          </div>
          <div class="summary">
            <span class="order-number-field">
              {{ t('sales.orders.fields.orderNumber') }}:
              <strong [class.preview-number]="isOrderNumberPreview()">{{ displayOrderNumber() }}</strong>
              @if (isOrderNumberPreview()) {
                <em class="preview-hint">{{ t('sales.orders.fields.orderNumberPreviewHint') }}</em>
              }
            </span>
            <span>{{ t('columns.status') }}: <strong>{{ status() || 'Draft' }}</strong></span>
            <span>{{ t('sales.orders.fields.channel') }}: <strong>{{ channelLabel(form.salesChannelId) }}</strong></span>
            <span>{{ t('sales.orders.fields.documentProfile') }}: <strong>{{ profileLabel(form.salesDocumentProfileId) }}</strong></span>
            <span>{{ t('sales.orders.fields.customer') }}: <strong>{{ customerLabel(form.customerAccountId) }}</strong></span>
            @if (sourceVisitPlanId()) {
              <span>{{ t('sales.orders.fields.visitPlan') }}: <strong><a [routerLink]="['/sales/distribution-visits', sourceVisitPlanId()!]">{{ sourceVisitPlanId()!.slice(0, 8) }}</a></strong></span>
            }
            <span>{{ t('sales.orders.fields.currency') }}: <strong>{{ form.currencyCode || '-' }}</strong></span>
            @if (submittedAtUtc()) {
              <span>{{ t('sales.orders.fields.submittedAt') }}: <strong>{{ submittedAtUtc() | ainDate:'datetime' }}</strong></span>
            }
            @if (cancelledAtUtc()) {
              <span>{{ t('sales.orders.fields.cancelledAt') }}: <strong>{{ cancelledAtUtc() | ainDate:'datetime' }}</strong></span>
            }
          </div>
        </header>

        <section class="card grid order-details">
          <label>
            <span>{{ t('sales.orders.fields.channel') }}</span>
            <p-select [options]="channels()" optionLabel="name" optionValue="id" [(ngModel)]="form.salesChannelId" [disabled]="!canEdit()" appendTo="body" />
          </label>
          <label>
            <span>{{ t('sales.orders.fields.documentProfile') }}</span>
            <p-select
              [options]="documentProfiles()"
              optionLabel="name"
              optionValue="id"
              [(ngModel)]="form.salesDocumentProfileId"
              (ngModelChange)="onDocumentProfileChange($event)"
              [disabled]="!canEdit()"
              appendTo="body" />
          </label>
          <label>
            <span>{{ t('sales.orders.fields.customer') }}</span>
            @for (selectorKey of [customerSelectorKey()]; track selectorKey) {
              <ain-customer-selector
                [(ngModel)]="form.customerAccountId"
                [disabled]="!canEdit() || lockCustomerFromVisit()" />
            }
          </label>
          <label>
            <span>{{ t('sales.orders.fields.currency') }}</span>
            <ain-currency-selector [(ngModel)]="form.currencyCode" [disabled]="!canEdit()" />
          </label>
        </section>

        <div class="tabs">
          <button type="button" [class.active]="activeTab() === 'items'" (click)="activeTab.set('items')">{{ t('sales.orders.tabs.items') }}</button>
          <button type="button" [class.active]="activeTab() === 'pricing'" (click)="activeTab.set('pricing')">{{ t('sales.orders.tabs.pricing') }}</button>
          <button type="button" [class.active]="activeTab() === 'delivery'" (click)="activeTab.set('delivery')">{{ t('sales.orders.tabs.delivery') }}</button>
          <button type="button" [class.active]="activeTab() === 'payment'" (click)="activeTab.set('payment')">{{ t('sales.orders.tabs.payment') }}</button>
          <button type="button" [class.active]="activeTab() === 'history'" (click)="activeTab.set('history')">{{ t('sales.orders.tabs.history') }}</button>
          <button type="button" [class.active]="activeTab() === 'attachments'" (click)="activeTab.set('attachments')">{{ t('sales.orders.tabs.attachments') }}</button>
        </div>

        @if (activeTab() === 'items') {
          <section class="card">
            @if (canEdit()) {
              <div class="line-toolbar">
                <button pButton type="button" [label]="t('sales.orders.lines.add')" (click)="addLine()"></button>
              </div>
            }
            <p-table [value]="lines()">
              <ng-template pTemplate="header">
                <tr>
                  <th>#</th>
                  <th>{{ t('sales.orders.lines.offering') }}</th>
                  <th>{{ t('sales.orders.lines.description') }}</th>
                  <th>{{ t('sales.orders.lines.quantity') }}</th>
                  <th>{{ t('sales.orders.lines.unitListPrice') }}</th>
                  <th>{{ t('sales.orders.lines.unitPrice') }}</th>
                  <th>{{ t('sales.orders.lines.lineTotal') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-line let-i="rowIndex">
                <tr>
                  <td>{{ i + 1 }}</td>
                  <td>
                    <p-select
                      [options]="offeringSelectOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [(ngModel)]="line.offeringId"
                      (ngModelChange)="onOfferingChange(line, $event)"
                      [disabled]="!canEdit()"
                      [showClear]="true"
                      appendTo="body" />
                  </td>
                  <td><input pInputText [(ngModel)]="line.description" [readonly]="!canEdit()" /></td>
                  <td><p-inputNumber [(ngModel)]="line.quantity" [readonly]="!canEdit()" /></td>
                  <td>{{ line.unitListPrice ?? 0 }}</td>
                  <td><p-inputNumber [(ngModel)]="line.unitPrice" [readonly]="!canEdit()" /></td>
                  <td>{{ line.quantity * line.unitPrice }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }

        @if (activeTab() === 'pricing') {
          <section class="card pricing-panel">
            @if (isCreateMode()) {
              <p class="hint">{{ t('sales.orders.pricing.saveFirst') }}</p>
            }
            <div class="pricing-toolbar">
              <label>
                <span>{{ t('sales.orders.pricing.priceList') }}</span>
                <p-select
                  [options]="priceListOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="selectedPriceListId"
                  [showClear]="true"
                  [placeholder]="t('sales.orders.pricing.useChannelDefault')"
                  appendTo="body" />
              </label>
              <label>
                <span>{{ t('sales.orders.pricing.couponCode') }}</span>
                <input pInputText [(ngModel)]="couponCode" [placeholder]="t('sales.orders.pricing.couponPlaceholder')" />
              </label>
              <button pButton type="button" [label]="t('sales.orders.pricing.recalculate')" (click)="recalculatePricing()" [disabled]="pricingBusy() || !canQuote()"></button>
              @if (canEdit() && lastQuote()) {
                <button pButton type="button" severity="secondary" [label]="t('sales.orders.pricing.apply')" (click)="applyPricing()" [disabled]="pricingBusy()"></button>
              }
              @if (!isCreateMode()) {
                <button pButton type="button" severity="help" [label]="t('sales.orders.pricing.evaluate')" (click)="evaluatePolicies()" [disabled]="pricingBusy()"></button>
              }
            </div>
            @if (pricingBusy()) {
              <ain-state type="loading" [title]="t('common.loading')" />
            } @else if (lastQuote()) {
              <div class="totals">
                <span>{{ t('sales.orders.pricing.subtotal') }}: <strong>{{ lastQuote()!.subtotal }}</strong></span>
                <span>{{ t('sales.orders.pricing.discount') }}: <strong>{{ lastQuote()!.totalDiscount }}</strong></span>
                @if ((lastQuote()!.promotionDiscount ?? 0) > 0) {
                  <span>{{ t('sales.orders.pricing.promotionDiscount') }}: <strong>{{ lastQuote()!.promotionDiscount }} ({{ lastQuote()!.couponCode }})</strong></span>
                }
                <span>{{ t('sales.orders.pricing.total') }}: <strong>{{ lastQuote()!.total }}</strong></span>
              </div>
              <p-table [value]="lastQuote()!.lines">
                <ng-template pTemplate="header">
                  <tr>
                    <th>#</th>
                    <th>{{ t('sales.orders.pricing.listUnitPrice') }}</th>
                    <th>{{ t('sales.orders.pricing.unitPrice') }}</th>
                    <th>{{ t('sales.orders.pricing.discountAmount') }}</th>
                    <th>{{ t('sales.orders.pricing.priceSource') }}</th>
                    <th>{{ t('sales.orders.lines.lineTotal') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.lineNumber }}</td>
                    <td>{{ row.listUnitPrice }}</td>
                    <td>{{ row.unitPrice }}</td>
                    <td>{{ row.discountAmount }}</td>
                    <td>{{ row.priceSource }}</td>
                    <td>{{ row.lineTotal }}</td>
                  </tr>
                </ng-template>
              </p-table>
            } @else {
              <ain-state type="empty" [title]="t('sales.orders.pricing.empty')" />
            }
            @if (policyResult()) {
              @if (policyResult()!.allowed) {
                <p class="message ok">{{ t('sales.orders.pricing.allowed') }}</p>
              } @else {
                <div class="violations">
                  <strong>{{ t('sales.orders.pricing.violations') }}</strong>
                  <ul>
                    @for (v of policyResult()!.violations; track v) {
                      <li>
                        <code>{{ v }}</code>
                        @if (canEdit()) {
                          <button
                            pButton
                            type="button"
                            size="small"
                            severity="secondary"
                            [label]="t('sales.orders.pricing.requestOverride')"
                            (click)="requestOverride(v)"></button>
                        }
                      </li>
                    }
                  </ul>
                </div>
              }
              @if ((policyResult()!.waivedViolations?.length ?? 0) > 0) {
                <div class="waived">
                  <strong>{{ t('sales.orders.pricing.waivedViolations') }}</strong>
                  <ul>
                    @for (v of policyResult()!.waivedViolations; track v) {
                      <li><code>{{ v }}</code></li>
                    }
                  </ul>
                </div>
              }
              @if ((policyResult()!.pendingOverrideCount ?? 0) > 0) {
                <p class="hint">{{ t('sales.orders.pricing.pendingOverrides') }}: {{ policyResult()!.pendingOverrideCount }}</p>
              }
            }
            @if (pricingHints().length > 0) {
              <div class="workbench-section">
                <h4>{{ t('sales.orders.pricing.hintsTitle') }}</h4>
                <ul class="hints">
                  @for (hint of pricingHints(); track hint.code) {
                    <li>
                      <strong>{{ hint.code }}</strong> — {{ hint.message }}
                      @if (hint.suggestedAction) {
                        <span class="hint-action">({{ hint.suggestedAction }})</span>
                      }
                    </li>
                  }
                </ul>
              </div>
            }
            @if (pricingOverrides().length > 0) {
              <div class="workbench-section">
                <h4>{{ t('sales.orders.pricing.overridesTitle') }}</h4>
                <p-table [value]="pricingOverrides()">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('sales.orders.pricing.overrideViolation') }}</th>
                      <th>{{ t('sales.orders.pricing.overrideReason') }}</th>
                      <th>{{ t('sales.orders.pricing.overrideStatus') }}</th>
                      <th></th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td><code>{{ row.violationCode }}</code></td>
                      <td>{{ row.reason }}</td>
                      <td>{{ inventoryStatusLabel(row.status) }}</td>
                      <td>
                        @if (row.status === 'Pending' && canEdit()) {
                          <button
                            pButton
                            type="button"
                            size="small"
                            [label]="t('sales.orders.pricing.approveOverride')"
                            (click)="approveOverride(row)"></button>
                          <button
                            pButton
                            type="button"
                            size="small"
                            severity="secondary"
                            [label]="t('sales.orders.pricing.rejectOverride')"
                            (click)="rejectOverride(row)"></button>
                        }
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              </div>
            }
            @if (pricingError()) {
              <p class="message error">{{ pricingError() }}</p>
            }
          </section>
        }

        @if (activeTab() === 'delivery') {
          <section class="card inventory-panel">
            <ain-info-hint
              [titleKey]="'sales.orders.inventory.help.title'"
              [descriptionKey]="'sales.orders.inventory.help.description'"
              [bulletKeys]="['sales.orders.inventory.help.bullet1', 'sales.orders.inventory.help.bullet2']" />
            @if (inventoryLoading()) {
              <ain-state type="loading" [title]="t('common.loading')" />
            } @else if (isCreateMode()) {
              <ain-state type="empty" [title]="t('sales.orders.inventory.saveFirst')" />
            } @else {
              @if (requiresInventoryLocation()) {
                <div class="grid">
                  <label>
                    <span>{{ t('catalog.inventory.warehouseId') }}</span>
                    <p-select [options]="warehouseOptions()" optionLabel="label" optionValue="value" [(ngModel)]="inventoryForm.warehouseId" (ngModelChange)="loadInventoryBins()" appendTo="body" />
                  </label>
                  <label>
                    <span>{{ t('catalog.inbound.targetBin') }}</span>
                    <p-select [options]="binOptions()" optionLabel="label" optionValue="value" [(ngModel)]="inventoryForm.binId" appendTo="body" />
                  </label>
                </div>
              }
              @if (inventoryStatus()) {
                <p-table [value]="inventoryStatus()!.lines">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>#</th>
                      <th>{{ t('columns.status') }}</th>
                      <th>{{ t('sales.orders.inventory.atp') }}</th>
                      <th>{{ t('sales.orders.inventory.reservation') }}</th>
                      <th>{{ t('sales.orders.inventory.movement') }}</th>
                      <th>{{ t('sales.orders.inventory.pickTask') }}</th>
                      <th>{{ t('sales.orders.inventory.failure') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ row.lineNumber }}</td>
                      <td>{{ row.status }}</td>
                      <td>{{ row.availableQuantity ?? '-' }}</td>
                      <td>
                        @if (row.catalogReservationId) {
                          <a [routerLink]="['/inventory/reservations']" [queryParams]="{ reservationId: row.catalogReservationId }">{{ t('sales.orders.inventory.viewReservation') }}</a>
                        } @else { - }
                      </td>
                      <td>
                        @if (row.inventoryMovementId) {
                          <a [routerLink]="['/inventory/movements', row.inventoryMovementId]">{{ t('sales.orders.inventory.viewMovement') }}</a>
                        } @else { - }
                      </td>
                      <td>
                        @if (row.pickTaskId) {
                          <a [routerLink]="['/inventory/warehouse-tasks']" [queryParams]="{ taskId: row.pickTaskId }">{{ t('sales.orders.inventory.viewTask') }}</a>
                        } @else { - }
                      </td>
                      <td>{{ inventoryFailureMessage(row.failureReason) }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              } @else {
                <ain-state type="empty" [title]="t('sales.orders.inventory.empty')" />
              }
              @if (inventoryError()) { <p class="message error">{{ inventoryError() }}</p> }
            }
          </section>
        }

        @if (activeTab() === 'payment') {
          <ain-state type="empty" [title]="t('sales.orders.tabs.paymentPlaceholder')" />
        }

        @if (activeTab() === 'attachments') {
          <ain-state type="empty" [title]="t('sales.orders.tabs.attachmentsPlaceholder')" />
        }

        @if (activeTab() === 'history') {
          @if (historyLoading()) {
            <ain-state type="loading" [title]="t('common.loading')" />
          } @else if (stateHistory().length === 0) {
            <ain-state type="empty" [title]="t('sales.orders.history.empty')" />
          } @else {
            <section class="card">
              <p-table [value]="stateHistory()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('sales.orders.history.occurredAt') }}</th>
                    <th>{{ t('sales.orders.history.transition') }}</th>
                    <th>{{ t('sales.orders.history.from') }}</th>
                    <th>{{ t('sales.orders.history.to') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.occurredAtUtc | ainDate:'datetime' }}</td>
                    <td>{{ row.transition }}</td>
                    <td>{{ row.fromStatus }}</td>
                    <td>{{ row.toStatus }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </section>
          }
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }

        @if (!isCreateMode() && copilotEntityId()) {
          <ain-sales-ai-copilot-panel entityType="SalesOrder" [entityId]="copilotEntityId()" defaultRecommendationType="NextBestOffer" />
        }

        <p-dialog
          [header]="t('sales.orders.pricing.requestOverride')"
          [visible]="overrideDialogOpen()"
          [modal]="true"
          [style]="{ width: '32rem', maxWidth: '95vw' }"
          (visibleChange)="onOverrideDialogVisibleChange($event)">
          <label class="override-reason-field">
            <span>{{ t('sales.orders.pricing.overrideReasonPrompt') }}</span>
            <textarea pTextarea rows="3" [(ngModel)]="overrideReason" class="w-full"></textarea>
          </label>
          <div class="dialog-footer">
            <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeOverrideDialog()"></button>
            <button pButton type="button" [label]="t('sales.orders.pricing.requestOverride')" (click)="confirmOverrideRequest()" [disabled]="!overrideReason.trim() || pricingBusy()"></button>
          </div>
        </p-dialog>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
    .order-header { display: grid; gap: 8px; margin-top: 10px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .summary {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      font-size: 14px;
      margin-top: 4px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    }
    .order-number-field { display: inline-flex; flex-wrap: wrap; align-items: baseline; gap: 6px; }
    .preview-number { color: #475569; font-style: italic; }
    .preview-hint { color: #64748b; font-size: 12px; font-style: normal; }
    .order-details { margin-top: 14px; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding: 8px 0; margin-top: 16px; }
    .tabs ~ .card,
    .tabs ~ ain-state { margin-top: 10px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .line-toolbar { margin-bottom: 10px; }
    .message.error { color: #b91c1c; }
    .message.ok { color: #15803d; }
    .pricing-panel { display: grid; gap: 10px; }
    .pricing-toolbar { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; }
    .pricing-toolbar label { min-width: 220px; }
    .totals { display: flex; flex-wrap: wrap; gap: 16px; font-size: 14px; }
    .violations { color: #b91c1c; font-size: 14px; }
    .violations li { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 6px; }
    .waived { color: #15803d; font-size: 14px; }
    .workbench-section h4 { margin: 0 0 8px; font-size: 14px; }
    .hints { margin: 0; padding-left: 18px; font-size: 13px; color: #334155; }
    .hint-action { color: #64748b; }
    .inventory-panel { display: grid; gap: 10px; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
    .override-reason-field { display: grid; gap: 6px; font-size: 13px; }
    .w-full { width: 100%; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesOrderDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly catalog = inject(CatalogService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly proformaContext = this.route.snapshot.data['documentContext'] === 'proforma';

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly copilotEntityId = signal<string | null>(null);
  readonly sourceVisitPlanId = signal<string | null>(null);
  readonly customerSelectorKey = signal(0);
  readonly errorMessage = signal('');
  readonly status = signal('Draft');
  readonly orderNumber = signal('');
  readonly previewOrderNumber = signal('');
  readonly submittedAtUtc = signal<string | null>(null);
  readonly cancelledAtUtc = signal<string | null>(null);
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly documentProfiles = signal<SalesDocumentProfileRecord[]>([]);
  readonly isProformaOrder = signal(false);
  private allDocumentProfiles: SalesDocumentProfileRecord[] = [];
  readonly customerLabels = signal<Map<string, string>>(new Map());
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly lines = signal<SalesOrderLinePayload[]>([]);
  readonly priceLists = signal<PriceListListItem[]>([]);
  readonly pricingBusy = signal(false);
  readonly pricingError = signal('');
  readonly lastQuote = signal<PricingQuoteResult | null>(null);
  readonly policyResult = signal<PolicyEvaluationResult | null>(null);
  readonly pricingHints = signal<PricingHintRecord[]>([]);
  readonly pricingOverrides = signal<PricingOverrideRecord[]>([]);
  readonly overrideDialogOpen = signal(false);
  overrideReason = '';
  private pendingViolationCode = '';
  selectedPriceListId: string | null = null;
  couponCode = '';
  private pricingSnapshotJson: string | null = null;

  offeringSelectOptions(): SelectOption[] {
    return this.offerings()
      .filter(offering => offering.salesEnabled)
      .map(offering => ({
        label: `${offering.code} - ${offering.title}`,
        value: offering.id
      }));
  }

  readonly activeTab = signal<'items' | 'pricing' | 'delivery' | 'payment' | 'history' | 'attachments'>('items');
  readonly stateHistory = signal<SalesOrderStateHistoryRecord[]>([]);
  readonly historyLoading = signal(false);
  readonly inventoryLoading = signal(false);
  readonly inventoryBusy = signal(false);
  readonly inventoryError = signal('');
  readonly inventoryStatus = signal<SalesOrderInventoryStatus | null>(null);
  private pendingFulfillmentIdempotencyKey = '';
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly bins = signal<CatalogWarehouseBin[]>([]);
  inventoryForm = { warehouseId: '', binId: '' };

  form = {
    salesChannelId: '',
    salesDocumentProfileId: '',
    customerAccountId: '',
    currencyCode: 'IRR'
  };

  priceListOptions(): SelectOption[] {
    return this.priceLists().map(list => ({
      label: `${list.code} - ${list.name}`,
      value: list.id
    }));
  }

  canQuote(): boolean {
    return !this.isCreateMode() && this.lines().length > 0;
  }

  ngOnInit(): void {
    bindSalesOrdersLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.refreshInventoryStatus(false)
    );

    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w), error: () => this.warehouses.set([]) });

    this.api.listPriceLists().subscribe({
      next: lists => this.priceLists.set(lists.filter(item => item.isActive)),
      error: () => this.priceLists.set([])
    });

    this.api.listChannels().subscribe({
      next: channels => {
        this.channels.set(channels);
        if (!this.form.salesChannelId && channels[0]) {
          this.form.salesChannelId = channels[0].id;
          this.form.currencyCode = channels[0].defaultCurrencyCode;
        }
      },
      error: () => this.channels.set([])
    });

    this.api.listDocumentProfiles().subscribe({
      next: profiles => {
        this.allDocumentProfiles = profiles.filter(item => item.isActive);
        if (!this.form.salesDocumentProfileId) {
          const selectable = this.selectableDocumentProfiles();
          if (selectable[0]) {
            this.form.salesDocumentProfileId = selectable[0].id;
            if (!this.form.currencyCode && selectable[0].defaultCurrencyCode) {
              this.form.currencyCode = selectable[0].defaultCurrencyCode;
            }
          }
        }
        this.updateProformaOrderFlag();
        this.refreshOrderNumberPreview();
      },
      error: () => {
        this.allDocumentProfiles = [];
        this.documentProfiles.set([]);
      }
    });

    this.financeApi.listAccountingCustomers().subscribe({
      next: rows => {
        const map = new Map<string, string>();
        for (const row of rows) {
          map.set(row.key, this.formatCustomerLabel(row));
        }
        this.customerLabels.set(map);
      },
      error: () => this.customerLabels.set(new Map())
    });

    this.catalog.listOfferings().subscribe({
      next: offerings => this.offerings.set(offerings),
      error: () => this.offerings.set([])
    });

    const orderId = this.resolveRouteOrderId();
    this.isCreateMode.set(!orderId || orderId === 'new');

    if (this.isCreateMode()) {
      this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(map => {
        this.applyCreateNavigationState(map);
      });
      this.applyCreateNavigationState(this.route.snapshot.queryParamMap);
      this.lines.set([{ description: '', quantity: 1, unitPrice: 0 }]);
      this.loading.set(false);
      return;
    }

    this.api.getOrder(orderId!).subscribe({
      next: order => {
        this.copilotEntityId.set(order.id);
        this.applyOrder(order);
        this.loadStateHistory(orderId!);
        this.refreshInventoryStatus();
      },
      error: () => {
        this.errorMessage.set(this.t('sales.orders.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  loadStateHistory(orderId: string): void {
    this.historyLoading.set(true);
    this.api.listOrderStateHistory(orderId).subscribe({
      next: rows => {
        this.stateHistory.set(rows);
        this.historyLoading.set(false);
      },
      error: () => {
        this.stateHistory.set([]);
        this.historyLoading.set(false);
      }
    });
  }

  canEdit(): boolean {
    return this.isCreateMode() || this.status() === 'Draft';
  }

  addLine(): void {
    this.lines.update(items => [...items, { description: '', quantity: 1, unitPrice: 0 }]);
  }

  onOfferingChange(line: SalesOrderLinePayload, offeringId: string | null): void {
    line.offeringId = offeringId || undefined;
    if (!offeringId) {
      return;
    }

    const offering = this.offerings().find(item => item.id === offeringId);
    if (!offering) {
      return;
    }

    line.description = offering.title;
    line.unitListPrice = offering.basePrice;
    line.unitPrice = offering.basePrice;
    this.lines.update(items => [...items]);
  }

  recalculatePricing(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId || this.isCreateMode()) {
      return;
    }

    this.pricingBusy.set(true);
    this.pricingError.set('');
    this.policyResult.set(null);
    this.api.quoteOrderPricing(orderId, this.selectedPriceListId ?? undefined, this.couponCode || undefined).subscribe({
      next: quote => {
        this.lastQuote.set(quote);
        this.pricingSnapshotJson = quote.snapshotJson ?? null;
        this.pricingBusy.set(false);
      },
      error: err => {
        this.pricingError.set(this.pricingErrorMessage(err));
        this.pricingBusy.set(false);
      }
    });
  }

  applyPricing(): void {
    const quote = this.lastQuote();
    if (!quote) {
      return;
    }

    this.pricingSnapshotJson = quote.snapshotJson;
    const updated = this.lines().map((line, index) => {
      const quoted = quote.lines.find(item => item.lineNumber === index + 1);
      if (!quoted) {
        return line;
      }
      return {
        ...line,
        unitListPrice: quoted.listUnitPrice,
        unitPrice: quoted.unitPrice,
        discountAmount: quoted.discountAmount,
        priceSource: quoted.priceSource
      };
    });
    this.lines.set(updated);
  }

  evaluatePolicies(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId || this.isCreateMode()) {
      return;
    }

    this.pricingBusy.set(true);
    this.pricingError.set('');
    this.api.evaluateOrderPolicies(orderId, this.selectedPriceListId ?? undefined, this.couponCode || undefined).subscribe({
      next: result => {
        this.policyResult.set(result);
        this.refreshPricingWorkbench(orderId);
        this.pricingBusy.set(false);
      },
      error: err => {
        this.pricingError.set(this.pricingErrorMessage(err));
        this.pricingBusy.set(false);
      }
    });
  }

  refreshPricingWorkbench(orderId: string): void {
    this.api.listOrderPricingHints(orderId, this.selectedPriceListId ?? undefined).subscribe({
      next: hints => this.pricingHints.set(hints),
      error: () => this.pricingHints.set([])
    });
    this.api.listOrderPricingOverrides(orderId).subscribe({
      next: rows => this.pricingOverrides.set(rows),
      error: () => this.pricingOverrides.set([])
    });
  }

  requestOverride(violationCode: string): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId || this.isCreateMode()) {
      return;
    }
    this.pendingViolationCode = violationCode;
    this.overrideReason = '';
    this.overrideDialogOpen.set(true);
  }

  onOverrideDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeOverrideDialog();
  }

  closeOverrideDialog(): void {
    this.overrideDialogOpen.set(false);
    this.pendingViolationCode = '';
    this.overrideReason = '';
  }

  confirmOverrideRequest(): void {
    const orderId = this.resolveRouteOrderId();
    const reason = this.overrideReason.trim();
    const violationCode = this.pendingViolationCode;
    if (!orderId || !reason || !violationCode) {
      return;
    }

    this.pricingBusy.set(true);
    this.api.requestPricingOverride(orderId, { violationCode, reason }).subscribe({
      next: () => {
        this.closeOverrideDialog();
        this.evaluatePolicies();
      },
      error: err => {
        this.pricingError.set(this.pricingErrorMessage(err));
        this.pricingBusy.set(false);
      }
    });
  }

  approveOverride(row: PricingOverrideRecord): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) {
      return;
    }
    this.pricingBusy.set(true);
    this.api.approvePricingOverride(orderId, row.id).subscribe({
      next: () => this.evaluatePolicies(),
      error: err => {
        this.pricingError.set(this.pricingErrorMessage(err));
        this.pricingBusy.set(false);
      }
    });
  }

  rejectOverride(row: PricingOverrideRecord): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) {
      return;
    }
    this.pricingBusy.set(true);
    this.api.rejectPricingOverride(orderId, row.id).subscribe({
      next: () => this.evaluatePolicies(),
      error: err => {
        this.pricingError.set(this.pricingErrorMessage(err));
        this.pricingBusy.set(false);
      }
    });
  }

  channelLabel(channelId: string): string {
    if (!channelId) {
      return '-';
    }
    return this.channels().find(item => item.id === channelId)?.name ?? '-';
  }

  profileLabel(profileId: string): string {
    if (!profileId) {
      return '-';
    }
    const profile = this.allDocumentProfiles.find(item => item.id === profileId)
      ?? this.documentProfiles().find(item => item.id === profileId);
    return profile ? `${profile.code} - ${profile.name}` : '-';
  }

  pageDescription(): string {
    return this.isProformaUi() ? this.t('sales.quotes.description') : this.t('sales.orders.description');
  }

  /** Proforma menu route or order whose document profile is Proforma. */
  isProformaUi(): boolean {
    return this.proformaContext || this.isProformaOrder();
  }

  canConvertProforma(): boolean {
    return this.isProformaOrder() && !this.isCreateMode() && this.status() !== 'Cancelled' && this.lines().length > 0;
  }

  printDocument(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) {
      return;
    }

    this.api.getOrderPrint(orderId).subscribe({
      next: doc => {
        if (!openHtmlDocumentForPrint(doc.html)) {
          this.errorMessage.set(
            doc.html?.trim()
              ? this.t('sales.orders.errorPrintPopupBlocked')
              : this.t('sales.orders.errorPrintDocument')
          );
        }
      },
      error: () => this.errorMessage.set(this.t('sales.orders.errorPrintDocument'))
    });
  }

  downloadDocument(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) {
      return;
    }

    this.api.getOrderPrint(orderId).subscribe({
      next: doc => {
        if (!downloadHtmlDocument(doc.html, doc.fileName || 'sales-document.html')) {
          this.errorMessage.set(this.t('sales.orders.errorPrintDocument'));
        }
      },
      error: () => this.errorMessage.set(this.t('sales.orders.errorPrintDocument'))
    });
  }

  convertToInvoice(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) {
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    this.api.convertProformaToInvoice(orderId).subscribe({
      next: order => void this.router.navigate(['/sales/orders', order.id]),
      error: () => {
        this.errorMessage.set(this.t('sales.quotes.errorConvertInvoice'));
        this.saving.set(false);
      }
    });
  }

  convertToContract(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) {
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    this.api.convertProformaToContract(orderId).subscribe({
      next: contract => void this.router.navigate(['/sales/contracts', contract.id]),
      error: () => {
        this.errorMessage.set(this.t('sales.quotes.errorConvertContract'));
        this.saving.set(false);
      }
    });
  }

  private resolveRouteOrderId(): string | null {
    const orderId = this.route.snapshot.paramMap.get('orderId');
    return orderId?.trim() ? orderId.trim() : null;
  }

  private selectableDocumentProfiles(): SalesDocumentProfileRecord[] {
    if (this.isProformaUi()) {
      return this.allDocumentProfiles.filter(item => item.documentKind === 'Proforma');
    }
    return this.allDocumentProfiles;
  }

  private applyDocumentProfileOptions(): void {
    this.documentProfiles.set(this.selectableDocumentProfiles());
  }

  private updateProformaOrderFlag(): void {
    const profileId = this.form.salesDocumentProfileId;
    const profile = this.allDocumentProfiles.find(item => item.id === profileId);
    this.isProformaOrder.set(profile?.documentKind === 'Proforma');
    this.applyDocumentProfileOptions();
  }

  private listBasePath(): string {
    return this.proformaContext ? '/sales/quotes' : '/sales/orders';
  }

  customerLabel(customerId: string): string {
    if (!customerId) {
      return '-';
    }
    return this.customerLabels().get(customerId) ?? customerId.slice(0, 8);
  }

  pageTitle(): string {
    if (this.isCreateMode() && this.sourceVisitPlanId()) {
      return this.t('sales.orders.form.createFromVisitTitle');
    }
    if (this.isProformaUi()) {
      return this.isCreateMode() ? this.t('sales.quotes.form.createTitle') : this.t('sales.quotes.form.editTitle');
    }
    return this.isCreateMode() ? this.t('sales.orders.form.createTitle') : this.t('sales.orders.form.editTitle');
  }

  backRouterLink(): string[] {
    const visitId = this.sourceVisitPlanId();
    if (visitId) {
      return ['/sales/distribution-visits', visitId];
    }
    return [this.listBasePath()];
  }

  backQueryParams(): Record<string, string> {
    return this.sourceVisitPlanId() ? {} : this.listQueryParams();
  }

  backLinkLabel(): string {
    return this.sourceVisitPlanId() ? this.t('sales.orders.action.backToVisit') : this.t('catalog.action.backToList');
  }

  lockCustomerFromVisit(): boolean {
    return !!this.sourceVisitPlanId();
  }

  displayOrderNumber(): string {
    const actual = this.orderNumber().trim();
    if (actual) {
      return actual;
    }

    const preview = this.previewOrderNumber().trim();
    return preview || '-';
  }

  isOrderNumberPreview(): boolean {
    return !this.orderNumber().trim() && !!this.previewOrderNumber().trim();
  }

  onDocumentProfileChange(profileId: string | null): void {
    if (!profileId) {
      this.previewOrderNumber.set('');
      this.updateProformaOrderFlag();
      return;
    }

    const profile = this.documentProfiles().find(item => item.id === profileId);
    this.updateProformaOrderFlag();
    if (profile?.defaultCurrencyCode && this.canEdit()) {
      this.form.currencyCode = profile.defaultCurrencyCode;
    }

    this.refreshOrderNumberPreview();
  }

  private refreshOrderNumberPreview(): void {
    if (!this.canEdit() || this.orderNumber().trim()) {
      this.previewOrderNumber.set('');
      return;
    }

    const profileId = this.form.salesDocumentProfileId?.trim();
    if (!profileId) {
      this.previewOrderNumber.set('');
      return;
    }

    this.api.previewOrderNumber(profileId).subscribe({
      next: preview => this.previewOrderNumber.set(preview),
      error: () => this.previewOrderNumber.set('')
    });
  }

  private applyCreateNavigationState(map: ParamMap): void {
    const visitPlanId = this.readQueryParam(map, 'visitPlanId');
    const customerAccountId =
      this.readQueryParam(map, 'customerAccountId') ?? this.readQueryParam(map, 'customerId');

    if (visitPlanId) {
      this.sourceVisitPlanId.set(visitPlanId);
    }

    if (customerAccountId) {
      this.setPrefillCustomer(customerAccountId);
      return;
    }

    if (visitPlanId) {
      this.api.getDistributionVisit(visitPlanId).subscribe({
        next: visit => {
          if (visit.customerAccountId) {
            this.setPrefillCustomer(visit.customerAccountId);
          }
        },
        error: () => {}
      });
    }
  }

  private setPrefillCustomer(customerAccountId: string): void {
    const id = customerAccountId.trim();
    if (!id) {
      return;
    }

    const previous = this.form.customerAccountId;
    this.form = { ...this.form, customerAccountId: id };
    if (previous !== id || this.customerSelectorKey() === 0) {
      this.customerSelectorKey.update(key => key + 1);
    }
    this.preloadCustomerLabel(id);
  }

  private preloadCustomerLabel(customerAccountId: string): void {
    this.financeApi.getPlatformCustomer(customerAccountId).subscribe({
      next: customer => {
        const label = customer.customerCode
          ? `${customer.displayName} #${customer.customerCode}`
          : customer.displayName;
        this.customerLabels.update(map => {
          const next = new Map(map);
          next.set(customer.id, label);
          next.set(customer.id.toLowerCase(), label);
          return next;
        });
      },
      error: () => {}
    });
  }

  private readQueryParam(map: ParamMap, key: string): string | null {
    const value = map.get(key);
    return value?.trim() ? value.trim() : null;
  }

  listQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    const source = this.route.snapshot.queryParams;
    for (const key of ['search', 'status', 'channelId', 'page', 'showFilters']) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) {
        params[key] = value;
      }
    }
    return params;
  }

  private formatCustomerLabel(customer: AccountingCustomerRecord): string {
    return customer.code ? `${customer.displayName} #${customer.code}` : customer.displayName;
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');
    const lines = this.lines();

    if (this.isCreateMode()) {
      const payload: CreateSalesOrderPayload = {
        ...this.form,
        idempotencyKey: crypto.randomUUID(),
        pricingSnapshotJson: this.pricingSnapshotJson,
        visitPlanId: this.sourceVisitPlanId(),
        lines
      };
      this.api.createOrder(payload).subscribe({
        next: order => void this.router.navigate([this.listBasePath(), order.id], { queryParams: this.listQueryParams() }),
        error: () => this.failSave()
      });
      return;
    }

    const orderId = this.resolveRouteOrderId()!;
    const payload: UpdateSalesOrderPayload = {
      ...this.form,
      pricingSnapshotJson: this.pricingSnapshotJson,
      lines
    };
    this.api.updateOrder(orderId, payload).subscribe({
      next: order => {
        this.applyOrder(order);
        this.saving.set(false);
      },
      error: () => this.failSave()
    });
  }

  submit(): void {
    const orderId = this.resolveRouteOrderId()!;
    this.saving.set(true);
    this.errorMessage.set('');

    const submitPayload = {
      idempotencyKey: crypto.randomUUID(),
      couponCode: this.couponCode?.trim() || undefined,
      warehouseId: this.inventoryForm.warehouseId || undefined,
      binId: this.inventoryForm.binId || undefined
    };

    const persistThenSubmit = () => {
      this.api.submitOrder(orderId, submitPayload).subscribe({
        next: order => {
          this.applyOrder(order);
          this.loadStateHistory(orderId);
          this.refreshInventoryStatus(false);
          this.saving.set(false);
        },
        error: err => this.failSave(err)
      });
    };

    if (this.status() !== 'Draft') {
      persistThenSubmit();
      return;
    }

    const snapshot = this.pricingSnapshotJson ?? this.lastQuote()?.snapshotJson ?? null;
    const payload: UpdateSalesOrderPayload = {
      ...this.form,
      pricingSnapshotJson: snapshot,
      lines: this.lines()
    };

    this.api.updateOrder(orderId, payload).subscribe({
      next: () => persistThenSubmit(),
      error: err => this.failSave(err)
    });
  }

  cancel(): void {
    const orderId = this.resolveRouteOrderId()!;
    this.saving.set(true);
    this.api.cancelOrder(orderId, { idempotencyKey: crypto.randomUUID(), reason: 'user_cancelled' }).subscribe({
      next: order => {
        this.applyOrder(order);
        this.loadStateHistory(orderId);
        this.saving.set(false);
      },
      error: () => this.failSave()
    });
  }

  private applyOrder(order: SalesOrderRecord): void {
    this.form = {
      salesChannelId: order.salesChannelId,
      salesDocumentProfileId: order.salesDocumentProfileId,
      customerAccountId: order.customerAccountId,
      currencyCode: order.currencyCode
    };
    if (order.visitPlanId) {
      this.sourceVisitPlanId.set(order.visitPlanId);
    }
    this.status.set(order.status);
    this.orderNumber.set(order.orderNumber);
    this.previewOrderNumber.set('');
    this.submittedAtUtc.set(order.submittedAtUtc ?? null);
    this.cancelledAtUtc.set(order.cancelledAtUtc ?? null);
    this.pricingSnapshotJson = order.pricingSnapshotJson ?? null;
    this.syncCouponCodeFromSnapshot();
    this.lines.set(
      order.lines.map(line => ({
        offeringId: line.offeringId,
        description: line.description,
        quantity: line.quantity,
        unitListPrice: line.unitListPrice,
        unitPrice: line.unitPrice,
        discountAmount: line.discountAmount,
        priceSource: line.priceSource
      }))
    );
    this.loading.set(false);
    this.saving.set(false);

    this.updateProformaOrderFlag();

    const orderId = this.resolveRouteOrderId();
    if (orderId && order.status === 'Draft') {
      this.refreshPricingWorkbench(orderId);
      this.refreshOrderNumberPreview();
    }
  }

  private syncCouponCodeFromSnapshot(): void {
    const raw = this.pricingSnapshotJson?.trim();
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as { couponCode?: string };
      if (typeof parsed.couponCode === 'string' && parsed.couponCode.trim()) {
        this.couponCode = parsed.couponCode.trim();
      }
    } catch {
      // ignore invalid snapshot json
    }
  }

  private pricingErrorMessage(err: unknown): string {
    const apiError = err && typeof err === 'object' && 'error' in err
      ? (err as { error?: { error?: string; Error?: string } }).error
      : undefined;
    const code = apiError?.error ?? apiError?.Error;
    if (code === 'coupon_not_found') {
      return this.t('sales.orders.pricing.couponNotFound');
    }
    if (code === 'coupon_campaign_mismatch') {
      return this.t('sales.orders.pricing.couponCampaignMismatch');
    }
    if (code === 'sales_pricing_policy_violation') {
      return this.t('sales.orders.pricing.policyBlocked');
    }
    return this.t('sales.orders.pricing.error');
  }

  private failSave(err?: unknown): void {
    const apiError = err && typeof err === 'object' && 'error' in err
      ? (err as { error?: { error?: string; Error?: string } }).error
      : undefined;
    const code = apiError?.error ?? apiError?.Error;
    if (code === 'coupon_not_found') {
      this.errorMessage.set(this.t('sales.orders.pricing.couponNotFound'));
    } else if (code === 'coupon_campaign_mismatch') {
      this.errorMessage.set(this.t('sales.orders.pricing.couponCampaignMismatch'));
    } else if (code === 'sales_pricing_policy_violation') {
      this.errorMessage.set(this.t('sales.orders.pricing.policyBlocked'));
    } else {
      this.errorMessage.set(this.t('sales.orders.errorSave'));
    }
    this.saving.set(false);
  }

  warehouseOptions(): SelectOption[] {
    return this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  }

  binOptions(): SelectOption[] {
    return this.bins().map(b => ({ label: b.code, value: b.id }));
  }

  loadInventoryBins(): void {
    if (!this.inventoryForm.warehouseId) {
      this.bins.set([]);
      return;
    }
    this.catalog.listWarehouseBins(this.inventoryForm.warehouseId).subscribe({
      next: bins => this.bins.set(bins),
      error: () => this.bins.set([])
    });
  }

  requiresInventoryLocation(): boolean {
    const profile = this.allDocumentProfiles.find(item => item.id === this.form.salesDocumentProfileId);
    return profile?.inventoryEffect === 'Issue';
  }

  canFulfill(): boolean {
    return !this.isCreateMode() && this.status() === 'Submitted' && this.requiresInventoryLocation();
  }

  refreshInventoryStatus(showLoading = true, clearError = true): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId || orderId === 'new' || this.isCreateMode()) return;
    if (showLoading) this.inventoryLoading.set(true);
    if (clearError) this.inventoryError.set('');
    this.api.getOrderInventoryStatus(orderId).subscribe({
      next: status => {
        this.inventoryStatus.set(status);
        this.pendingFulfillmentIdempotencyKey = status.lines
          .find(line => line.status === 'CatalogIssuedPendingLink')
          ?.fulfillIdempotencyKey ?? '';
        const firstLine = status.lines.find(l => l.warehouseId);
        if (firstLine?.warehouseId && !this.inventoryForm.warehouseId) {
          this.inventoryForm.warehouseId = firstLine.warehouseId;
          this.loadInventoryBins();
        }
        if (firstLine?.binId && !this.inventoryForm.binId) {
          this.inventoryForm.binId = firstLine.binId;
        }
        this.inventoryLoading.set(false);
      },
      error: () => {
        this.inventoryStatus.set(null);
        this.pendingFulfillmentIdempotencyKey = '';
        this.inventoryLoading.set(false);
      }
    });
  }

  fulfill(): void {
    const orderId = this.resolveRouteOrderId();
    if (!orderId) return;
    this.inventoryBusy.set(true);
    this.inventoryError.set('');
    const idempotencyKey = this.pendingFulfillmentIdempotencyKey || crypto.randomUUID();
    this.pendingFulfillmentIdempotencyKey = idempotencyKey;
    this.api.fulfillOrder(orderId, {
      idempotencyKey,
      warehouseId: this.inventoryForm.warehouseId || undefined,
      binId: this.inventoryForm.binId || undefined
    }).subscribe({
      next: result => {
        this.status.set(result.orderStatus);
        this.pendingFulfillmentIdempotencyKey = '';
        this.inventoryBusy.set(false);
        this.refreshInventoryStatus(false);
      },
      error: err => {
        const apiError = err && typeof err === 'object' && 'error' in err
          ? (err as { error?: { error?: string } }).error
          : undefined;
        const errorCode = apiError?.error;
        if (errorCode !== 'sales_link_save_failed') {
          this.pendingFulfillmentIdempotencyKey = '';
        }
        this.inventoryError.set(
          errorCode === 'sales_link_save_failed'
            ? this.t('sales.orders.inventory.salesLinkSaveFailed')
            : this.inventoryFailureMessage(errorCode)
        );
        this.inventoryBusy.set(false);
        this.refreshInventoryStatus(false, false);
      }
    });
  }

  fulfillActionLabel(): string {
    return this.pendingFulfillmentIdempotencyKey
      ? this.t('sales.orders.inventory.retryLink')
      : this.t('sales.orders.inventory.fulfill');
  }

  inventoryStatusLabel(status: string): string {
    const key = `sales.orders.inventory.status.${status}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  inventoryFailureMessage(errorCode?: string | null): string {
    if (!errorCode) return '-';
    const key = `sales.orders.inventory.error.${errorCode}`;
    const translated = this.t(key);
    return translated === key ? this.t('sales.orders.inventory.fulfillFailed') : translated;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
